export const useValidationResultsStore = defineStore('validationResults', () => {
  // Map of botanical name (lowercase) to validation result
  const validationResults = ref(new Map<string, {
    originalName: string
    matchedName: string | null
    family: string | null
    genus: string | null
    author: string | null
    source: string | null
    score: number
  }>())

  // Map of hashed species list to availability results
  const availabilityResults = ref(new Map<string, {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    panelId?: string
  }>())

  // Map of hashed species list to biosecurity results
  const biosecurityResults = ref(new Map<string, {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    panelId?: string
  }>())

  // Map of hashed species list to weed results
  const weedResults = ref(new Map<string, {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    projectLocation?: string
    panelId?: string
  }>())

  // Reactive getters
  const getValidationResults = computed(() => validationResults.value)
  
  const getFamilyForName = (botanicalName: string): string | null => {
    const result = validationResults.value.get(botanicalName.toLowerCase())
    return result?.family || null
  }

  const getGenusForName = (botanicalName: string): string | null => {
    const result = validationResults.value.get(botanicalName.toLowerCase())
    return result?.genus || null
  }

  const hasValidationResult = (botanicalName: string): boolean => {
    return validationResults.value.has(botanicalName.toLowerCase())
  }

  // Availability result methods
  const hashSpeciesNames = (speciesNames: string[], projectId: string, panelId?: string): string => {
    // Sort names to ensure consistent hashing regardless of order
    const sortedNames = [...speciesNames].sort().map(name => name.toLowerCase())
    const combined = `${projectId}:${panelId || 'global'}:${sortedNames.join('|')}`
    
    // Simple hash function (could use crypto.subtle.digest for better hashing)
    let hash = 0
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
  }

  // Weed-specific hash function that includes location
  const hashWeedSpeciesNames = (speciesNames: string[], projectId: string, projectLocation?: string, panelId?: string): string => {
    // Sort names to ensure consistent hashing regardless of order
    const sortedNames = [...speciesNames].sort().map(name => name.toLowerCase())
    const combined = `${projectId}:${panelId || 'global'}:${projectLocation || 'no-location'}:${sortedNames.join('|')}`
    
    // Simple hash function (could use crypto.subtle.digest for better hashing)
    let hash = 0
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
  }

  const getAvailabilityResults = (speciesNames: string[], projectId: string, maxCacheAge: number = 86400, panelId?: string): {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    panelId?: string
  } | null => {
    const cacheKey = hashSpeciesNames(speciesNames, projectId, panelId)
    const cached = availabilityResults.value.get(cacheKey)
    
    if (!cached) return null
    
    // Check if cache is still valid
    const now = Date.now()
    const ageInSeconds = (now - cached.timestamp) / 1000
    
    if (ageInSeconds > maxCacheAge) {
      availabilityResults.value.delete(cacheKey)
      return null
    }
    
    // Verify the cached data matches the requested species (in case of hash collision)
    const cachedSpeciesSorted = [...cached.speciesNames].sort().map(name => name.toLowerCase())
    const requestedSpeciesSorted = [...speciesNames].sort().map(name => name.toLowerCase())
    
    if (JSON.stringify(cachedSpeciesSorted) !== JSON.stringify(requestedSpeciesSorted) || 
        cached.projectId !== projectId) {
      return null
    }
    
    console.log(`[ValidationResultsStore] Found cached availability results for ${speciesNames.length} species (age: ${Math.round(ageInSeconds)}s)`)
    return cached
  }

  const setAvailabilityResults = (
    speciesNames: string[], 
    projectId: string, 
    results: Record<string, any>,
    panelId?: string
  ): void => {
    const cacheKey = hashSpeciesNames(speciesNames, projectId, panelId)
    const cacheEntry = {
      results,
      timestamp: Date.now(),
      speciesNames: [...speciesNames], // Store copy
      projectId,
      panelId
    }
    
    availabilityResults.value.set(cacheKey, cacheEntry)
    console.log(`[ValidationResultsStore] Cached availability results for ${speciesNames.length} species (key: ${cacheKey})`)
  }

  const hasAvailabilityResults = (speciesNames: string[], projectId: string, maxCacheAge: number = 86400, panelId?: string): boolean => {
    return getAvailabilityResults(speciesNames, projectId, maxCacheAge, panelId) !== null
  }

  const clearAvailabilityResults = (): void => {
    availabilityResults.value.clear()
    console.log('[ValidationResultsStore] Cleared all availability results')
  }

  // Biosecurity result methods
  const getBiosecurityResults = (speciesNames: string[], projectId: string, maxCacheAge: number = 86400, panelId?: string): {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    panelId?: string
  } | null => {
    const cacheKey = hashSpeciesNames(speciesNames, projectId, panelId)
    const cached = biosecurityResults.value.get(cacheKey)
    
    if (!cached) return null
    
    // Check if cache is still valid
    const now = Date.now()
    const ageInSeconds = (now - cached.timestamp) / 1000
    
    if (ageInSeconds > maxCacheAge) {
      biosecurityResults.value.delete(cacheKey)
      return null
    }
    
    // Verify the cached data matches the requested species (in case of hash collision)
    const cachedSpeciesSorted = [...cached.speciesNames].sort().map(name => name.toLowerCase())
    const requestedSpeciesSorted = [...speciesNames].sort().map(name => name.toLowerCase())
    
    if (JSON.stringify(cachedSpeciesSorted) !== JSON.stringify(requestedSpeciesSorted) || 
        cached.projectId !== projectId) {
      return null
    }
    
    console.log(`[ValidationResultsStore] Found cached biosecurity results for ${speciesNames.length} species (age: ${Math.round(ageInSeconds)}s)`)
    return cached
  }

  const setBiosecurityResults = (
    speciesNames: string[], 
    projectId: string, 
    results: Record<string, any>,
    panelId?: string
  ): void => {
    const cacheKey = hashSpeciesNames(speciesNames, projectId, panelId)
    const cacheEntry = {
      results,
      timestamp: Date.now(),
      speciesNames: [...speciesNames], // Store copy
      projectId,
      panelId
    }
    
    biosecurityResults.value.set(cacheKey, cacheEntry)
    console.log(`[ValidationResultsStore] Cached biosecurity results for ${speciesNames.length} species (key: ${cacheKey})`)
  }

  const hasBiosecurityResults = (speciesNames: string[], projectId: string, maxCacheAge: number = 86400, panelId?: string): boolean => {
    return getBiosecurityResults(speciesNames, projectId, maxCacheAge, panelId) !== null
  }

  const clearBiosecurityResults = (): void => {
    biosecurityResults.value.clear()
    console.log('[ValidationResultsStore] Cleared all biosecurity results')
  }

  // Weed result methods
  const getWeedResults = (speciesNames: string[], projectId: string, projectLocation?: string, maxCacheAge: number = 86400, panelId?: string): {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    projectLocation?: string
    panelId?: string
  } | null => {
    const cacheKey = hashWeedSpeciesNames(speciesNames, projectId, projectLocation, panelId)
    const cached = weedResults.value.get(cacheKey)
    
    if (!cached) return null
    
    // Check if cache is still valid
    const now = Date.now()
    const ageInSeconds = (now - cached.timestamp) / 1000
    
    if (ageInSeconds > maxCacheAge) {
      weedResults.value.delete(cacheKey)
      return null
    }
    
    // Verify the cached data matches the requested species and location (in case of hash collision)
    const cachedSpeciesSorted = [...cached.speciesNames].sort().map(name => name.toLowerCase())
    const requestedSpeciesSorted = [...speciesNames].sort().map(name => name.toLowerCase())
    
    if (JSON.stringify(cachedSpeciesSorted) !== JSON.stringify(requestedSpeciesSorted) || 
        cached.projectId !== projectId ||
        cached.projectLocation !== projectLocation) {
      return null
    }
    
    console.log(`[ValidationResultsStore] Found cached weed results for ${speciesNames.length} species (age: ${Math.round(ageInSeconds)}s, location: ${projectLocation || 'no-location'})`)
    return cached
  }

  const setWeedResults = (
    speciesNames: string[], 
    projectId: string, 
    results: Record<string, any>,
    projectLocation?: string,
    panelId?: string
  ): void => {
    const cacheKey = hashWeedSpeciesNames(speciesNames, projectId, projectLocation, panelId)
    const cacheEntry = {
      results,
      timestamp: Date.now(),
      speciesNames: [...speciesNames], // Store copy
      projectId,
      projectLocation,
      panelId
    }
    
    weedResults.value.set(cacheKey, cacheEntry)
    console.log(`[ValidationResultsStore] Cached weed results for ${speciesNames.length} species (key: ${cacheKey}, location: ${projectLocation || 'no-location'})`)
  }

  const hasWeedResults = (speciesNames: string[], projectId: string, projectLocation?: string, maxCacheAge: number = 86400, panelId?: string): boolean => {
    return getWeedResults(speciesNames, projectId, projectLocation, maxCacheAge, panelId) !== null
  }

  const clearWeedResults = (): void => {
    weedResults.value.clear()
    console.log('[ValidationResultsStore] Cleared all weed results')
  }

  // Clear weed results for a specific project location (for cache invalidation)
  const clearWeedResultsForLocation = (projectId: string, projectLocation?: string): void => {
    const keysToDelete: string[] = []
    weedResults.value.forEach((cacheEntry, key) => {
      if (cacheEntry.projectId === projectId && cacheEntry.projectLocation === projectLocation) {
        keysToDelete.push(key)
      }
    })
    keysToDelete.forEach(key => weedResults.value.delete(key))
    console.log(`[ValidationResultsStore] Cleared weed results for project ${projectId} at location: ${projectLocation || 'no-location'} (${keysToDelete.length} entries)`)
  }

  // Panel management methods
  const getAllAvailabilityResultsByPanel = (): Record<string, {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    panelId?: string
  }[]> => {
    const resultsByPanel: Record<string, any[]> = {}
    
    availabilityResults.value.forEach((cacheEntry, key) => {
      const panelId = cacheEntry.panelId || 'global'
      if (!resultsByPanel[panelId]) {
        resultsByPanel[panelId] = []
      }
      resultsByPanel[panelId].push(cacheEntry)
    })
    
    return resultsByPanel
  }

  const getAllBiosecurityResultsByPanel = (): Record<string, {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    panelId?: string
  }[]> => {
    const resultsByPanel: Record<string, any[]> = {}
    
    biosecurityResults.value.forEach((cacheEntry, key) => {
      const panelId = cacheEntry.panelId || 'global'
      if (!resultsByPanel[panelId]) {
        resultsByPanel[panelId] = []
      }
      resultsByPanel[panelId].push(cacheEntry)
    })
    
    return resultsByPanel
  }

  const getAllWeedResultsByPanel = (): Record<string, {
    results: Record<string, any>
    timestamp: number
    speciesNames: string[]
    projectId: string
    projectLocation?: string
    panelId?: string
  }[]> => {
    const resultsByPanel: Record<string, any[]> = {}
    
    weedResults.value.forEach((cacheEntry, key) => {
      const panelId = cacheEntry.panelId || 'global'
      if (!resultsByPanel[panelId]) {
        resultsByPanel[panelId] = []
      }
      resultsByPanel[panelId].push(cacheEntry)
    })
    
    return resultsByPanel
  }

  const clearResultsForPanel = (panelId: string): void => {
    // Clear availability results for specific panel
    const availKeysToDelete: string[] = []
    availabilityResults.value.forEach((cacheEntry, key) => {
      if (cacheEntry.panelId === panelId) {
        availKeysToDelete.push(key)
      }
    })
    availKeysToDelete.forEach(key => availabilityResults.value.delete(key))

    // Clear biosecurity results for specific panel
    const biosecKeysToDelete: string[] = []
    biosecurityResults.value.forEach((cacheEntry, key) => {
      if (cacheEntry.panelId === panelId) {
        biosecKeysToDelete.push(key)
      }
    })
    biosecKeysToDelete.forEach(key => biosecurityResults.value.delete(key))

    // Clear weed results for specific panel
    const weedKeysToDelete: string[] = []
    weedResults.value.forEach((cacheEntry, key) => {
      if (cacheEntry.panelId === panelId) {
        weedKeysToDelete.push(key)
      }
    })
    weedKeysToDelete.forEach(key => weedResults.value.delete(key))

    console.log(`[ValidationResultsStore] Cleared all results for panel: ${panelId}`)
  }

  // Actions - Atomic updates
  const updateValidationResult = (botanicalName: string, result: {
    originalName: string
    matchedName: string | null
    family: string | null
    genus: string | null
    author: string | null
    source: string | null
    score: number
  }) => {
    const key = botanicalName.toLowerCase()
    validationResults.value.set(key, result)
    console.log(`[ValidationResultsStore] Updated single validation result for: ${botanicalName}`)
  }

  const updateMultipleValidationResults = (results: Map<string, {
    originalName: string
    matchedName: string | null
    family: string | null
    genus: string | null
    author: string | null
    source: string | null
    score: number
  }>, merge: boolean = true) => {
    if (merge) {
      // Merge new results with existing ones
      results.forEach((result, key) => {
        validationResults.value.set(key, result)
      })
      console.log(`[ValidationResultsStore] Merged ${results.size} validation results (total: ${validationResults.value.size})`)
    } else {
      // Replace all results
      validationResults.value = new Map(results)
      console.log(`[ValidationResultsStore] Replaced all validation results with ${results.size} entries`)
    }
  }

  const removeValidationResult = (botanicalName: string) => {
    const key = botanicalName.toLowerCase()
    const removed = validationResults.value.delete(key)
    if (removed) {
      console.log(`[ValidationResultsStore] Removed validation result for: ${botanicalName}`)
    }
    return removed
  }

  // Legacy method for backward compatibility
  const updateValidationResults = (results: Map<string, {
    originalName: string
    matchedName: string | null
    family: string | null
    genus: string | null
    author: string | null
    source: string | null
    score: number
  }>) => {
    updateMultipleValidationResults(results, false) // Use replace behavior for backward compatibility
  }

  const clearValidationResults = () => {
    validationResults.value.clear()
    console.log('[ValidationResultsStore] Cleared all validation results')
  }

  return {
    // State
    validationResults: readonly(validationResults),
    availabilityResults: readonly(availabilityResults),
    biosecurityResults: readonly(biosecurityResults),
    weedResults: readonly(weedResults),
    
    // Getters
    getValidationResults,
    
    // Validation Actions
    getFamilyForName,
    getGenusForName,
    hasValidationResult,
    updateValidationResult,
    updateMultipleValidationResults,
    removeValidationResult,
    updateValidationResults, // Legacy method
    clearValidationResults,
    
    // Availability Actions
    hashSpeciesNames,
    getAvailabilityResults,
    setAvailabilityResults,
    hasAvailabilityResults,
    clearAvailabilityResults,
    
    // Biosecurity Actions
    getBiosecurityResults,
    setBiosecurityResults,
    hasBiosecurityResults,
    clearBiosecurityResults,
    
    // Weed Actions
    hashWeedSpeciesNames,
    getWeedResults,
    setWeedResults,
    hasWeedResults,
    clearWeedResults,
    clearWeedResultsForLocation,
    
    // Panel Management Actions
    getAllAvailabilityResultsByPanel,
    getAllBiosecurityResultsByPanel,
    getAllWeedResultsByPanel,
    clearResultsForPanel
  }
}) 